import {
  PEOPLE_SETTINGS_READ_ROLES,
  PEOPLE_SETTINGS_SECTIONS,
  PEOPLE_SETTINGS_SECTION_PATHS,
  PEOPLE_SETTINGS_TOOL_READ_ROLES,
} from '@/lib/settings/people-settings';
import { DOCUMENT_LIBRARY_ROLES } from '@/lib/documents/library-access';
/**
 * Settings IA — tenant self-service control plane.
 *
 * Canonical navigation for `/settings/**`. Legacy card grid is retired in
 * favour of this grouped rail + optional settings root overview.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  CreditCard,
  Layers,
  Plug,
  ShieldCheck,
  SlidersHorizontal,
  UsersRound,
  Bell,
  Archive,
  ShieldAlert,
  FileSignature,
  ClipboardList,
  ClipboardCheck,
  Workflow,
  History,
  Settings as SettingsIcon,
  Banknote,
  Import,
  Globe,
  CalendarClock,
  ListChecks,
  Send,
  Boxes,
  Flag,
  KeyRound,
  Smartphone,
  Landmark,
  UserCheck,
  BadgeCheck,
  DollarSign,
  ScrollText,
  Wand2,
  BookOpen,
  Sparkles,
} from 'lucide-react';
import {
  SETTINGS_FINANCE_ROLES,
  SETTINGS_INTEGRATIONS_HUB_ROLES,
  SETTINGS_OWN_NOTIFICATION_ROLES,
  SETTINGS_PRICING_ROLES,
  SETTINGS_PRICING_AGREEMENTS_ROLES,
  SETTINGS_PAYROLL_PROVIDER_ROLES,
  SETTINGS_PAY_ROLE_ROLES,
  SETTINGS_RETENTION_ROLES,
  SETTINGS_SERVICES_AUSTRALIA_ROLES,
  SETTINGS_SCREENING_ROLES,
  SETTINGS_SYSTEM_OPERATOR_ROLES,
  SETTINGS_TENANT_ADMIN_ROLES,
  SETTINGS_XERO_ROLES,
  SIGNATURE_TEMPLATE_SETTINGS_ROLES,
} from '@/lib/settings/settings-access-roles';
import { B2G_INTEGRATION_ROUTE_ROLES } from '@/lib/integrations/government/b2g-access';

export interface SettingsLeaf {
  href: string;
  label: string;
  icon: LucideIcon;
  note?: string;
  matchPaths?: readonly string[];
  roles?: readonly string[];
  /** Preserve existing saved delegation when regrouping a leaf. */
  delegationClusterId?: string;
}

export interface SettingsCluster {
  id: string;
  title: string;
  icon: LucideIcon;
  description: string;
  items: readonly SettingsLeaf[];
}

const ADMIN = SETTINGS_TENANT_ADMIN_ROLES;
const FINANCE = SETTINGS_FINANCE_ROLES;
const PAYROLL_PROVIDER = SETTINGS_PAYROLL_PROVIDER_ROLES;
const PAY_ROLES = SETTINGS_PAY_ROLE_ROLES;
const PRICING = SETTINGS_PRICING_ROLES;
const OPERATOR = SETTINGS_SYSTEM_OPERATOR_ROLES;
const SERVICES_AUSTRALIA = SETTINGS_SERVICES_AUSTRALIA_ROLES;
const XERO = SETTINGS_XERO_ROLES;
const INTEGRATIONS_HUB = SETTINGS_INTEGRATIONS_HUB_ROLES;
const SIGNATURE = SIGNATURE_TEMPLATE_SETTINGS_ROLES;
const SCREENING = SETTINGS_SCREENING_ROLES;

/** Cross-section discoverability links surfaced in the settings rail (self-gated targets). */
export const SETTINGS_CROSS_SECTION_LEAVES = [
  '/tasks/signatures',
  '/compliance/documents',
  '/admin/system/sso',
  '/admin/system/scim',
  '/admin/system/mobile-devices',
  '/admin/system/signature-corrections',
  '/admin/system/engine-versions',
  '/admin/system/workforce-audit',
  '/settings/onboarding/worker/base',
  '/people/compliance/screening',
  '/pricing/rate-cards',
  '/pricing/agreements',
] as const;

/** Top-level platform settings routes that must appear in the v2 IA. */
export const PLATFORM_SETTINGS_IA_REQUIRED_HREFS = [
  '/settings/organisation',
  '/settings/registration-providers',
  '/settings/facilities',
  '/settings/facility-config',
  '/settings/portal',
  '/settings/holidays',
  '/settings/leave-policies',
  '/settings/funding-authorisations',
  '/settings/users',
  '/settings/approvals',
  '/settings/system',
  '/settings/payroll/activation',
  '/settings/workflows',
  '/settings/workflows/closeout',
  '/settings/assessments',
  '/settings/compliance',
  '/settings/document-templates',
  '/settings/onboarding',
  '/settings/onboarding/client-packs',
  '/settings/onboarding/readiness',
  '/settings/onboarding/induction-policies',
  '/settings/onboarding-packs',
  '/settings/cert-allowance-registry',
  '/settings/roster',
  '/settings/cm-task-types',
  '/settings/signature-templates',
  '/settings/notifications',
  '/settings/notification-routing',
  '/settings/notification-routing/wizard',
  '/settings/notification-preferences',
  '/settings/notification-audit',
  '/settings/notifications/log',
  '/settings/integrations',
  '/settings/integrations/xero',
  '/settings/integrations/xero/wizard',
  '/settings/integrations/xero/backfill',
  '/settings/payroll',
  '/settings/payroll/eba',
  '/settings/payroll/profile',
  '/settings/payroll/provider-responsibilities',
  '/settings/payroll/pay-roles',
  '/settings/payroll/aba-sender',
  '/settings/import',
  '/settings/security',
  '/settings/data-management',
  '/settings/retention',
  '/settings/billing',
  '/settings/modules',
  ...SETTINGS_CROSS_SECTION_LEAVES,
] as const;

export const SETTINGS_IA: readonly SettingsCluster[] = [
  {
    id: 'organisation',
    title: 'Organisation',
    icon: Building2,
    description: 'Provider identity, facilities, regions, and family portal.',
    items: [
      {
        href: '/settings/organisation',
        label: 'Organisation',
        icon: Building2,
        note: 'ABN, registration, branding',
        roles: ADMIN,
      },
      {
        href: '/settings/registration-providers',
        label: 'Registration & providers',
        icon: Layers,
        note: 'Statement identity and delivery sub-providers',
        roles: ADMIN,
      },
      {
        href: '/settings/facilities',
        label: 'Facilities',
        icon: Building2,
        roles: ADMIN,
      },
      {
        href: '/settings/facility-config',
        label: 'Facility configuration',
        icon: SlidersHorizontal,
        roles: ADMIN,
      },
      {
        href: '/settings/portal',
        label: 'Client portal',
        icon: Globe,
        note: 'Family and client-facing sections',
        roles: ADMIN,
      },
      {
        href: '/settings/holidays',
        label: 'Public holidays',
        icon: CalendarClock,
        roles: ADMIN,
      },
      {
        href: '/settings/leave-policies',
        label: 'Leave policies',
        icon: ClipboardList,
        note: 'Adopt registry definitions and publish effective periods',
        roles: ADMIN,
      },
      {
        href: '/settings/funding-authorisations',
        label: 'Funding authorisations',
        icon: Landmark,
        note: 'Funder caps and purchase orders',
        roles: FINANCE,
      },
    ],
  },
  {
    id: 'people-settings',
    title: 'People',
    icon: UsersRound,
    description: 'Onboarding, documents, policies, learning and legal checks.',
    items: PEOPLE_SETTINGS_SECTIONS.map((section) => ({
      href: `/settings/people#${section.id}`,
      label: section.label,
      icon: UsersRound,
      roles: PEOPLE_SETTINGS_READ_ROLES,
      matchPaths: PEOPLE_SETTINGS_SECTION_PATHS[section.id],
    })),
  },
  {
    id: 'people',
    title: 'Access & roles',
    icon: UsersRound,
    description: 'Users, approvals, timezone, and enterprise sign-in.',
    items: [
      {
        href: '/settings/users',
        label: 'Users & roles',
        icon: UsersRound,
        roles: ADMIN,
      },
      {
        href: '/settings/approvals',
        label: 'Approval rules',
        icon: ListChecks,
        roles: ADMIN,
      },
      {
        href: '/settings/system',
        label: 'System preferences',
        icon: SettingsIcon,
        note: 'Timezone, navigation visibility',
        roles: ADMIN,
      },
      {
        href: '/settings/payroll/activation',
        label: 'New payroll activation',
        icon: Banknote,
        roles: OPERATOR,
      },
      {
        href: '/admin/system/sso',
        label: 'Single sign-on (SAML)',
        icon: KeyRound,
        roles: ADMIN,
      },
      {
        href: '/admin/system/scim',
        label: 'SCIM provisioning',
        icon: KeyRound,
        roles: ADMIN,
      },
      {
        href: '/admin/system/mobile-devices',
        label: 'Mobile devices',
        icon: Smartphone,
        note: 'Personal vs shared-station deployment profile for the care-worker app',
        roles: ADMIN,
      },
      {
        href: '/admin/system/signature-corrections',
        label: 'Signature corrections',
        icon: FileSignature,
        roles: ADMIN,
      },
      // path-to-go S23 — operator audit surfaces previously orphaned from IA.
      {
        href: '/admin/system/engine-versions',
        label: 'Engine versions',
        icon: History,
        note: 'Wage-engine build attribution in paid window (regulator lens)',
        roles: ADMIN,
      },
      {
        href: '/admin/system/workforce-audit',
        label: 'Workforce audit',
        icon: UsersRound,
        note: 'Award / role / agency mismatches for workforce compliance',
        roles: ADMIN,
      },
    ],
  },
  {
    id: 'care-config',
    title: 'Care configuration',
    icon: SlidersHorizontal,
    description: 'Care workflows, assessments, compliance and roster setup.',
    items: [
      {
        href: '/settings/workflows',
        label: 'Workflow templates',
        icon: Workflow,
        roles: ADMIN,
      },
      {
        href: '/settings/workflows/closeout',
        label: 'Workflow closeout',
        icon: Workflow,
        roles: ADMIN,
      },
      {
        href: '/settings/assessments',
        label: 'Assessment library',
        icon: ClipboardCheck,
        note: 'Platform instruments and provider templates',
        roles: ADMIN,
      },
      {
        href: '/settings/compliance',
        label: 'Compliance settings',
        icon: ShieldCheck,
        note: 'Provider targets, review cadences and complaint wording',
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'QUALITY_DIRECTOR',
          'QUALITY_OFFICER',
          'COMPLIANCE_OFFICER',
          'FACILITY_MANAGER',
        ],
      },
      {
        href: '/people/compliance/screening',
        label: 'Required screening',
        icon: BadgeCheck,
        note: 'Worker credentials and renewal cadence',
        roles: SCREENING,
      },
      {
        href: '/settings/cert-allowance-registry',
        label: 'Cert allowance registry',
        icon: ShieldCheck,
        roles: ADMIN,
      },
      {
        href: '/settings/roster',
        label: 'Roster adoption mode',
        icon: CalendarClock,
        note: 'Time-boxed go-live attestations and evidence backfill',
        roles: ADMIN,
      },
      {
        href: '/settings/service-execution',
        label: 'Service execution (v2)',
        icon: ClipboardList,
        note: 'Per-pack evidence cutover: off, shadow, enforce',
        roles: ADMIN,
      },
      {
        href: '/settings/cm-task-types',
        label: 'CM task types',
        icon: ClipboardList,
        roles: ADMIN,
      },
    ],
  },
  {
    id: 'documents-signatures',
    title: 'Documents & signatures',
    icon: FileSignature,
    description:
      'Set up worker documents, client signing, onboarding packs and safe document storage.',
    items: [
      {
        href: '/settings/document-templates',
        label: 'Document templates',
        icon: ClipboardList,
        note: 'Worker templates, document types and visibility to care workers',
        roles: PEOPLE_SETTINGS_TOOL_READ_ROLES,
      },
      {
        href: '/settings/signature-templates',
        delegationClusterId: 'care-config',
        label: 'Signature templates & signing settings',
        icon: FileSignature,
        note: 'Client agreements, consents and signing options',
        roles: SIGNATURE,
      },
      {
        href: '/settings/onboarding-packs',
        label: 'Worker onboarding packs',
        icon: UserCheck,
        note: 'Choose documents for each worker onboarding pack',
        roles: PEOPLE_SETTINGS_TOOL_READ_ROLES,
      },
      {
        href: '/settings/onboarding/client-packs',
        delegationClusterId: 'care-config',
        label: 'Client onboarding packs',
        icon: UserCheck,
        note: 'Choose agreements and consents for client onboarding',
        roles: ADMIN,
      },
      {
        href: '/settings/retention',
        delegationClusterId: 'security',
        label: 'Retention & reminders',
        icon: Archive,
        note: 'Review how long records are kept and protected',
        roles: SETTINGS_RETENTION_ROLES,
      },
      {
        href: '/settings/data-management',
        delegationClusterId: 'security',
        label: 'Virus scanning & data management',
        note: 'Check scanner posture, waiting files and the last sweep',
        icon: ShieldCheck,
        roles: ADMIN,
      },
      {
        href: '/tasks/signatures',
        label: 'Documents and signatures queue',
        note: 'Send, follow up and review signed evidence',
        icon: FileSignature,
        roles: [
          'SYSTEM_ADMIN',
          'TENANT_ADMIN',
          'FACILITY_MANAGER',
          'FINANCE_OFFICER',
          'COORDINATOR',
          'CASE_MANAGER',
          'QUALITY_OFFICER',
          'CLINICAL_LEAD',
          'REGISTERED_NURSE',
          'REGION_MANAGER',
          'HR_MANAGER',
        ],
      },
      {
        href: '/compliance/documents',
        label: 'Document library',
        icon: ShieldCheck,
        note: 'Browse, file and share managed documents',
        roles: DOCUMENT_LIBRARY_ROLES,
      },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: Bell,
    description: 'Channels, routing rules, digests, and delivery evidence.',
    items: [
      {
        href: '/settings/notifications',
        label: 'Channels',
        icon: Send,
        note: 'Email, SMS, in-app',
        roles: ADMIN,
      },
      {
        href: '/settings/notification-routing',
        label: 'Routing rules',
        icon: Workflow,
        roles: ADMIN,
      },
      {
        href: '/settings/notification-routing/wizard',
        label: 'Routing wizard',
        icon: Sparkles,
        note: 'Guided setup for alert recipients',
        roles: ADMIN,
      },
      {
        // Personal, not tenant configuration: every internal role may manage
        // their own channels. Label matches the page title (Help Centre
        // lockstep); "Daily digests" described only one of the categories.
        href: '/settings/notification-preferences',
        label: 'Notification preferences',
        icon: CalendarClock,
        roles: SETTINGS_OWN_NOTIFICATION_ROLES,
      },
      {
        href: '/settings/notification-audit',
        label: 'Audit log',
        icon: History,
        roles: ADMIN,
      },
      {
        href: '/settings/notifications/log',
        label: 'Delivery log',
        icon: History,
        roles: ADMIN,
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    icon: Plug,
    description: 'Finance connections and data import.',
    items: [
      {
        href: '/settings/integrations',
        label: 'Government connections',
        icon: Plug,
        roles: INTEGRATIONS_HUB,
      },
      {
        href: '/settings/integrations/xero',
        label: 'Xero',
        icon: Plug,
        note: 'Payroll and invoice mapping',
        roles: XERO,
      },
      {
        href: '/settings/integrations/xero/wizard',
        label: 'Xero mapping wizard',
        icon: Wand2,
        note: 'Employees, rates, accounts, and dry-runs',
        roles: XERO,
      },
      {
        href: '/settings/integrations/xero/backfill',
        label: 'Xero invoice backfill',
        icon: History,
        note: 'Historical invoice preparation',
        roles: XERO,
      },
      {
        href: '/settings/payroll',
        label: 'Payroll settings',
        icon: Banknote,
        note: 'Identity, export, time capture, and review',
        roles: PAYROLL_PROVIDER,
      },
      {
        href: '/settings/payroll/eba',
        label: 'Payroll · enterprise agreements',
        icon: Banknote,
        roles: ADMIN,
      },
      {
        href: '/settings/payroll/profile',
        label: 'Payroll · profile',
        icon: ScrollText,
        note: 'Award coverage, pay calendar, export target',
        roles: PAYROLL_PROVIDER,
      },
      {
        href: '/settings/payroll/pay-roles',
        label: 'Payroll · pay roles',
        icon: BadgeCheck,
        note: 'Role catalogue and assignment coverage',
        roles: PAY_ROLES,
      },
      {
        href: '/settings/payroll/provider-responsibilities',
        label: 'Payroll · provider responsibilities',
        icon: ClipboardCheck,
        note: 'Statura, provider, shared, and manual-review ownership',
        roles: PAYROLL_PROVIDER,
      },
      {
        href: '/settings/payroll/aba-sender',
        label: 'Payroll · ABA sender',
        icon: Banknote,
        roles: FINANCE,
      },
      {
        href: '/settings/import',
        label: 'Data import',
        icon: Import,
        roles: ADMIN,
      },
      {
        href: '/settings/integrations/services-australia',
        label: 'Services Australia',
        icon: ShieldCheck,
        roles: SERVICES_AUSTRALIA,
      },
      {
        href: '/settings/integrations/services-australia/conformance',
        label: 'Services Australia · conformance',
        icon: ShieldCheck,
        roles: SERVICES_AUSTRALIA,
      },
      {
        href: '/settings/integrations/dhda',
        label: 'DHDA B2G',
        icon: ShieldCheck,
        roles: B2G_INTEGRATION_ROUTE_ROLES,
      },
      {
        href: '/settings/webhooks',
        label: 'Webhooks (operator)',
        icon: Plug,
        roles: OPERATOR,
      },
      {
        href: '/settings/webhooks/docs',
        label: 'Webhook API docs',
        icon: BookOpen,
        roles: OPERATOR,
      },
    ],
  },
  {
    id: 'security',
    title: 'Security & data',
    icon: ShieldCheck,
    description: 'Sign-in security, exports and privacy controls.',
    items: [
      {
        href: '/settings/security',
        label: 'Security',
        icon: ShieldAlert,
        roles: ADMIN,
      },
    ],
  },
  {
    id: 'billing',
    title: 'Billing & plan',
    icon: CreditCard,
    description: 'Subscription, modules, and platform plan.',
    items: [
      {
        href: '/settings/billing',
        label: 'Billing',
        icon: CreditCard,
        roles: FINANCE,
      },
      {
        href: '/pricing/rate-cards',
        label: 'Rate cards',
        icon: DollarSign,
        note: 'Billable service rates and charge codes',
        roles: PRICING,
      },
      {
        href: '/pricing/agreements',
        label: 'Service agreements',
        icon: ScrollText,
        note: 'Client pricing and agreement templates',
        roles: SETTINGS_PRICING_AGREEMENTS_ROLES,
      },
      {
        href: '/settings/modules',
        label: 'Modules',
        icon: Boxes,
        roles: ADMIN,
      },
      {
        href: '/settings/feature-flags',
        label: 'Feature flags',
        icon: Flag,
        roles: OPERATOR,
      },
    ],
  },
];

export function flattenSettingsIa(): ReadonlyArray<
  SettingsLeaf & { clusterId: string; clusterTitle: string }
> {
  const out: Array<SettingsLeaf & { clusterId: string; clusterTitle: string }> = [];
  for (const cluster of SETTINGS_IA) {
    for (const item of cluster.items) {
      out.push({ ...item, clusterId: cluster.id, clusterTitle: cluster.title });
    }
  }
  return out;
}

export function isLeafVisibleForRole(
  leaf: Pick<SettingsLeaf, 'roles'>,
  viewerRole: string | null | undefined,
): boolean {
  if (!leaf.roles || leaf.roles.length === 0) return true;
  if (!viewerRole) return false;
  return leaf.roles.includes(viewerRole);
}

const CI_FREE_VISIBLE_PREFIXES: readonly string[] = [
  '/settings/organisation',
  '/settings/facilities',
  '/settings/users',
  '/settings/security',
  '/settings/system',
  '/admin/system/sso',
  '/admin/system/scim',
  '/admin/system/mobile-devices',
  '/admin/system/signature-corrections',
  '/admin/system/engine-versions',
  '/admin/system/workforce-audit',
  '/settings/onboarding/worker/base',
];

export function isLeafVisibleForCiFree(leaf: Pick<SettingsLeaf, 'href'>): boolean {
  return CI_FREE_VISIBLE_PREFIXES.some(
    (prefix) => leaf.href === prefix || leaf.href.startsWith(`${prefix}/`),
  );
}

export function findClusterForPath(
  pathname: string,
): { cluster: SettingsCluster; item: SettingsLeaf } | null {
  const requested = pathname;
  pathname = pathname.split('#')[0]!;
  let best: { cluster: SettingsCluster; item: SettingsLeaf; length: number } | null = null;
  for (const cluster of SETTINGS_IA) {
    for (const item of cluster.items) {
      const href = item.href.split('#')[0]!;
      const lengths = [href, ...(item.matchPaths ?? [])]
        .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
        .map((path) => path.length);
      if (lengths.length === 0) continue;
      const length = requested === item.href ? item.href.length + 1000 : Math.max(...lengths);
      if (!best || length > best.length) best = { cluster, item, length };
    }
  }
  return best ? { cluster: best.cluster, item: best.item } : null;
}
